import { VideoAccessError, VideoStartEvent, VideoStateChangeEvent } from '../../types'
import FlvJs from 'mpegts.js'
import { BaseVideoPlayer } from './BaseVideoPlayer'
import { isErtDebugModeEnabled, getVideoIdentifier } from '../../rrwebUtils'
import { createRef, RefObject } from 'react'
import ReactDOM from 'react-dom/client'
import { flushSync } from 'react-dom'
import WarningIcon from '../../../../../../assets/images/icons/vdp-warning-icon.svg'
import { CameraModels } from '@simplisafe/compass-web'
import { getRenderCallback } from './PlayerError'
import { LoggerName, namedLogger } from '../../../../../../utils/logger'

const logger = namedLogger(LoggerName.ErtFlvPlayer)

export class FlvPlayer extends BaseVideoPlayer {
  constructor(
    initialParent: HTMLElement,
    videoStartEvent: VideoStartEvent,
    videoStateChangeEvents: VideoStateChangeEvent[],
    cameraModel?: string,
    options?: { mockVideoElement?: HTMLVideoElement }
  ) {
    const videoShellRef = createRef<HTMLDivElement>()
    const videoElementRef = createRef<HTMLVideoElement>()
    renderVideo(initialParent, videoShellRef, videoElementRef, videoStartEvent, cameraModel)

    // this allows us to instantiate this player in tests
    videoElementRef.current = options?.mockVideoElement ?? videoElementRef.current

    const mediaDataSource: FlvJs.MediaDataSource = {
      type: 'flv',
      isLive: false,
      url: videoStartEvent.streamingSessionURL,
      cors: true
    }

    const config: FlvJs.Config = {
      headers: {
        Authorization: `Bearer ${videoStartEvent.token?.accessToken}`
      }
    }

    const flvPlayer = FlvJs.createPlayer(mediaDataSource, config)
    flvPlayer.attachMediaElement(videoElementRef.current!)
    flvPlayer.load()

    flvPlayer.on(FlvJs.Events.METADATA_ARRIVED, metadata => {
      logger.debug('flv metadata', metadata)
    })
    flvPlayer.on(FlvJs.Events.ERROR, (error, detail, info) => {
      logger.debug('flv error', error, detail, info)

      // This will prevent the player from spinning indefinitely if it tries to load a video it doesn't have access to
      // biome-ignore lint/suspicious/noDoubleEquals: because we don't have a guarantee that the code is a number, we intentionally are using == to allow type coercion
      if ([401, 403].some(statusCode => statusCode == info?.code)) {
        flvPlayer.unload()

        videoStartEvent.videoAccessError = {
          videoOmitted: false,
          status: info.code,
          error: 'Error',
          message: 'Video could not be loaded'
        } satisfies VideoAccessError

        const renderCallback = getRenderCallback(initialParent, videoShellRef, videoStartEvent)
        flushSync(renderCallback)

        this.videoElement = undefined
        if (videoShellRef.current) {
          this.videoShell = videoShellRef.current
        }
      }
    })

    if (!videoShellRef.current || !videoElementRef.current) {
      throw new Error('Video shell or element not properly initialized')
    }

    super(initialParent, videoShellRef.current, videoElementRef.current, videoStartEvent, videoStateChangeEvents)
  }
}

const MAIN_VIDEO_CANVAS = 'main-video-canvas'

const renderVideo = (
  appendTo: HTMLElement,
  videoShellRef: RefObject<HTMLDivElement | null>,
  videoElementRef: RefObject<HTMLVideoElement | null>,
  videoStartEvent: VideoStartEvent,
  cameraModel?: string
) => {
  flushSync(() => {
    ReactDOM.createRoot(appendTo).render(
      <div
        style={{
          position: 'relative'
        }}
        ref={videoShellRef}
        title={getVideoIdentifier(videoStartEvent)}
      >
        <video
          ref={videoElementRef}
          preload="auto"
          className="flv-player"
          controls={isErtDebugModeEnabled()}
          style={{ width: '100%', height: '100%', objectFit: 'contain', objectPosition: 'center' }}
        />
        {shouldShowVdpWarning(cameraModel, videoStartEvent) && (
          <div
            style={{
              position: 'absolute',
              bottom: '0.5rem',
              left: '0.5rem',
              lineHeight: 0,
              width: videoStartEvent.videoPlaceholderName === MAIN_VIDEO_CANVAS ? 'unset' : '28px',
              overflow: 'hidden',
              backgroundColor: '#c63636',
              borderRadius: '0.5rem',
              padding: '0.375rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              color: 'white',
              height: 'auto',
              gap: '0.5rem',
              cursor: 'default'
            }}
          >
            <img src={WarningIcon} alt="Warning Icon" />
            <div style={{ flex: '0 1 50%', whiteSpace: 'nowrap' }}>VDP</div>
          </div>
        )}
      </div>
    )
  })
}

// VDP live streams have been unreliable in ERT: switching between live and recorded
// playback can hang, and the mini-player has shown footage from earlier than the live
// moment. The badge warns the reviewer not to treat this feed as an exact record of
// what the specialist saw. (https://simplisafe.atlassian.net/browse/MOPS-2254)
function shouldShowVdpWarning(cameraModel?: string, videoEvent?: VideoStartEvent): boolean {
  return (videoEvent?.isLive ?? false) && cameraModel === CameraModels.VideoDoorbellPro
}
